import { createImage } from '#src/app/shared/utils/dom';

// the design's disclosure scrim (AI-Badge spec): black at 18% over the whole image, under the mark
const OVERLAY_FILL = 'rgba(0, 0, 0, 0.18)';

export type WatermarkPlacement = Readonly<{
	corner: 'top-left' | 'top-right';
	/** Mark width as a fraction of the image width (height follows the asset's aspect ratio). */
	markWidthRatio: number;
	/**
	 * Top and side padding in rendered CSS px — the mark's padding mirrors the page's fixed
	 * text/content padding, so unlike the mark itself it must not scale with the image.
	 */
	paddingCss: number;
}>;

async function loadImageElement(src: string): Promise<HTMLImageElement> {
	// oxlint-disable-next-line promise/avoid-new
	return new Promise((resolve, reject) => {
		const image = createImage();
		// requests cross-origin read permission so drawing the CDN image does not taint the canvas
		image.crossOrigin = 'anonymous';
		image.onload = () => {
			resolve(image);
		};
		image.onerror = () => {
			reject(new Error('Error loading image'));
		};
		image.src = src;
	});
}

/**
 * Bakes the AI-disclosure treatment into an image: draws the image at its natural size on a
 * canvas, darkens it with the design's scrim (which keeps the white mark legible on light
 * photos), overlays the watermark in the given corner, and encodes the result as a JPEG blob
 * (browser default quality) so the mark survives download.
 *
 * The mark is sized as a fraction of the image (it scales with it); the padding is a CSS px
 * value converted through `renderedWidth` — the image's on-page width at composite time — so it
 * renders at the specced size. Without a usable `renderedWidth` (e.g. a CSS-hidden instance
 * measures 0) the padding falls back to raw px against the file.
 *
 * Rejects when either image fails to load, the 2d context is unavailable, or the canvas is
 * tainted (the image host did not allow cross-origin reads).
 */
export async function compositeWatermark(
	imageSrc: string,
	watermarkSrc: string,
	placement: WatermarkPlacement,
	renderedWidth: number | undefined,
): Promise<Blob> {
	const [image, watermark] = await Promise.all([loadImageElement(imageSrc), loadImageElement(watermarkSrc)]);

	const canvas = document.createElement('canvas');
	canvas.width = image.naturalWidth;
	canvas.height = image.naturalHeight;
	const context = canvas.getContext('2d');
	if (!context) throw new Error('Canvas 2d context unavailable');

	context.drawImage(image, 0, 0);
	context.fillStyle = OVERLAY_FILL;
	context.fillRect(0, 0, canvas.width, canvas.height);

	const watermarkWidth = canvas.width * placement.markWidthRatio;
	const watermarkHeight = watermarkWidth * (watermark.naturalHeight / watermark.naturalWidth);
	const padding = renderedWidth ? placement.paddingCss * (canvas.width / renderedWidth) : placement.paddingCss;
	const x = placement.corner === 'top-left' ? padding : canvas.width - watermarkWidth - padding;
	context.drawImage(watermark, x, padding, watermarkWidth, watermarkHeight);

	// oxlint-disable-next-line promise/avoid-new
	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) resolve(blob);
			else reject(new Error('Canvas toBlob returned no data'));
		}, 'image/jpeg');
	});
}
